import React from 'react';
import { Observe } from 'rewire-core';
import classNames from 'classnames';
import { WithStyle, FormView } from 'rewire-ui';
import Color from 'color';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Paper from '@material-ui/core/Paper';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AddUdfIcon from '@material-ui/icons/PlaylistAdd';
import UdfEditor from '../settings/UdfEditor';
import batchDetailsModel from './BatchDetailsModel';
import { withStyles } from '../utility/styles';
import { IBatchModel } from './CreateBatchModel';

const styles = (theme: Theme) => ({
  root: {
    minWidth: '350px',
    maxWidth: '1024px',
    width: '100%',
    margin: '8px',
    padding: '16px',
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
    transition: theme.transitions.create(['width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
  },
  headerContainer: {
    marginBottom: '20px',
  },
  headerTitleBar: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    '& > :first-child': {
      marginRight: '15px',
      marginBottom: '5px',
    },
  },
  headerTitle: {
    display: 'flex',
    flex: '1',
    alignSelf: 'center',
  },
  cardViewContainer: {},
  cardViewButtonsContainer: {
    display: 'flex',
    '& > :first-child': {
      borderRadius: '4px 0 0 4px',
    },
    '& > :last-child': {
      borderRadius: '0 4px 4px 0',
    },
  },
  cardViewButton: {
    textTransform: 'none',
    marginRight: '5px',
    maxWidth: '150px',
    borderRadius: '4px',
  },
  cardViewButtonDisabled: {},
  divider: {
    marginTop: '12px',
  },
  content: {
    padding: '0px 8px',
    alignItems: 'center',
    flex: 1,
  },
  fieldContainer: {
    width: '100%',
    marginBottom: '20px',
    '& > div': {
      display: 'flex',
    },
  },
  addIcon: {
    flex: '0 0 auto',
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.palette.success.main,
    '&:hover': {
      color: Color(theme.palette.success.main).darken(0.3).toString(),
    },
  },
  deleteIcon: {
    flex: '0 0 auto',
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.palette.error.main,
    '&:hover': {
      color: Color(theme.palette.success.main).darken(0.3).toString(),
    },
  },
  disabled: {
    color: 'lightgray !important',
    fontStyle: 'italic',
  },
  buttoncounter: {
    fontSize: '2em',
    maxWidth: '150px',
    flex: '0 0 auto',
    alignSelf: 'flex-end',
  },
  counterContainer: {
    alignSelf: 'flex-end',
    borderRadius: '5px',
    backgroundColor: '#e0e0e0',
    display: 'flex',
    alignItems: 'center',
  },
  counterTitle: {
    width: '710px',
    display: 'inline-block',
    padding: '8px',
    marginBottom: '0px',
  },
  canAddTimecards: {
    backgroundColor: '#ccf5cc',
  },
  cannotAddTimecards: {
    backgroundColor: '#f5cccc',
  },
  counterNumberContainer: {
    fontSize: '2.5em',
    maxWidth: '150px',
    flex: '0 0 auto',
    alignSelf: 'flex-end',
    minWidth: '64px',
    fontWeight: '500',
    lineHeight: '1.4',
  },
  counterNumber: {
    display: 'inline-flex',
    justifyContent: 'center',
    padding: '6px 16px',
    borderRadius: '5px',
  },
});

interface IBatchDetailsProps {
  model: IBatchModel;
}

type BatchDetailsProps = WithStyle<ReturnType<typeof styles>, IBatchDetailsProps>;

class BatchDetailsView extends React.PureComponent<BatchDetailsProps> {
  constructor(props: BatchDetailsProps) {
    super(props);
  }

  componentWillUnmount() {
    batchDetailsModel.reset();
  }

  render() {
    const { classes, model } = this.props;

    return (
      <FormView className={classes.content} form={batchDetailsModel.form} onSubmit={model.actionFn('save')}>
        <Paper className={classes.root} elevation={4}>
          <Observe
            render={() => {
              const otherClass = model.timecardsToAdd === 0 ? classes.cannotAddTimecards : classes.canAddTimecards;

              return (
                <>
                  <div className={classNames(classes.counterContainer, otherClass)}>
                    {model.counterExplanation ? (
                      <Typography variant="caption" display="inline" paragraph={true} className={classes.counterTitle}>
                        {model.counterExplanation}
                      </Typography>
                    ) : null}
                    <span className={classes.counterNumberContainer}>
                      <span className={classNames(classes.counterNumber, otherClass)}>{model.timecardsToAdd}</span>
                    </span>
                  </div>
                  <div key={model.form.field.areas.name} className={classes.fieldContainer}>
                    <model.form.field.areas.Editor />
                  </div>
                  <div key={model.form.field.groups.name} className={classes.fieldContainer}>
                    <model.form.field.groups.Editor />
                  </div>
                  <div key={model.form.field.startOfWeeks.name} className={classes.fieldContainer}>
                    <model.form.field.startOfWeeks.Editor />
                  </div>
                </>
              );
            }}
          />
        </Paper>

        <Paper className={classes.root} elevation={4}>
          <div className={classes.headerContainer}>
            <div className={classes.headerTitleBar}>
              <Observe
                render={() => {
                  return (
                    <div style={{ display: 'flex' }}>
                      <Typography variant="h6" className={classes.headerTitle}>
                        {model.detailsTitle}
                      </Typography>
                      <Button arial-label="Udfs" title="User Defined Fields" onClick={batchDetailsModel.loadUdfEditor}>
                        <AddUdfIcon />
                      </Button>
                    </div>
                  );
                }}
              />
            </div>
            <Divider className={classes.divider} />
          </div>

          <Observe
            render={() => (
              <>
                {batchDetailsModel.form.fields.map((field: any) => (
                  <div key={field.name} className={classes.fieldContainer}>
                    <field.Editor />
                  </div>
                ))}
              </>
            )}
          />

          <UdfEditor udfEditor={batchDetailsModel.udfEditor} />
          {/* <Observe render={() => <InfoPanelFooter isDisabled={batchDetailsModel.isDisabled} actions={batchDetailsModel.actions} />} /> */}
        </Paper>
      </FormView>
    );
  }
}

export default withStyles<IBatchDetailsProps>(styles, BatchDetailsView);
